import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Grid2,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { WorkerInfo } from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import { UserRole, useSessionStorage } from "IF/userSession";

import { SettingWorkerForm } from "./SettingWorkerForm";
import SettingWorkerStatus from "./SettingWorkerStatus";
import { MessageModal } from "./MessageModal";
import ConfirmationModal from "./ConfirmationModal";
import { convertNumberToFullWidthDigits, createTitle } from "Utils/format";
import { DataContext } from "Contexts/DataProvider";
import { Key } from "@mui/icons-material";

export const SettingWorker = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  const theme = useTheme();

  // form初期データ
  const initData: WorkerInfo = {
    id: 0,
    tagId: "",
    name: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    createUser: userId,
    createDatetime: new Date(),
    updateUser: userId,
    updateDatetime: new Date(),
  };
  // form
  const [inputFormData, setInputFormData] = useState<WorkerInfo>(initData);
  const [searchResults, setSearchResults] = useState<WorkerInfo[]>([]);
  const [workerList, setWorkerList] = useState<WorkerInfo[]>([]);
  // 検索時タグID
  const [searchTagId, setSearchTagId] = useState("");
  // 検索済フラグ
  const [isSearch, setIsSearch] = useState(false);
  const [bodyWidth, setBodyWidth] = useState(600);
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  // messageモーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");

  const closeModal = () => {
    setModalOpen(false);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: string,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[0]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[0]?.text ?? "");
      toggleMenu("設定");
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const updateWidth = () => {
      const windowWidth = window.innerWidth;
      const adjustedWidth =
        windowWidth <= 900 ? windowWidth - 330 : windowWidth - 550;
      setBodyWidth(adjustedWidth);
    };

    const updateHeight = () => {
      const windowHeight = window.innerHeight;
      const calculatedHeight = windowHeight - 130;
      setTableBodyHeight(calculatedHeight);
    };

    // 初期表示時とリサイズ時に計算
    updateWidth();
    window.addEventListener("resize", updateWidth);

    updateHeight();
    window.addEventListener("resize", updateHeight);

    // 登録済の作業者を取得
    getWorkerList(companyCode);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", updateWidth);
      window.removeEventListener("resize", updateHeight);
    };
  }, [companyCode]);

  // 登録済TagIdのデータ取得処理
  const getWorkerList = async (companyCode: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/WorkerSetting/GetWorkerList`,
        {
          params: { schema: companyCode },
        }
      );
      setWorkerList(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("登録者リストの取得に失敗しました。");
      console.error("登録者リストの取得に失敗しました。", error);
    }
  };

  // 検索タグID編集時処理
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    value = value.toUpperCase();
    setSearchTagId(value);
  };

  // 検索ボタン押下時処理
  const handleSearchButton = async () => {
    if (!searchTagId.trim()) {
      setModalOpen(true);
      setMessage("タグIDを入力してください。");
      return;
    }
    handleSearch();
  };

  // 検索処理
  const handleSearch = async (ResearchTagId?: string) => {
    const tagId = ResearchTagId ? ResearchTagId : searchTagId;
    // API呼び出し
    try {
      const response = await axios.get(
        `${API_BASE_URL}/WorkerSetting/GetWorker`,
        {
          params: { tagId: tagId, schema: companyCode },
        }
      );
      setSearchResults(response.data);
      setIsSearch(true);
      setInputFormData({
        ...inputFormData,
        tagId: ResearchTagId ? ResearchTagId : searchTagId,
      });
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("作業者情報の登録に失敗しました。");
        console.error("作業者情報の登録に失敗しました。", error);
      }
    }
  };

  // 新規登録ボタン押下時処理
  const handleInput = async (inputData: WorkerInfo) => {
    try {
      // 入力チェック
      if (!validateRequired(inputData)) return;
      // 開始～終了の時系列整合性チェック
      if (!validateChronologicalOrder(inputData)) return;
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/WorkerSetting/PostWorker`,
        {
          schema: companyCode,
          workerData: inputData,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("作業者情報が登録されました。");
        // 再検索用
        const tagId = inputData.tagId;
        setSearchTagId(tagId);
        handleSearch(tagId);
        // 登録済の作業者を取得
        getWorkerList(companyCode);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("作業者情報の登録に失敗しました。");
        console.error("作業者情報の登録に失敗しました。", error);
      }
    }
  };

  // 更新ボタン押下時処理
  const handleUpdate = async (updateData: WorkerInfo) => {
    try {
      // 入力チェック
      if (!isChange(updateData)) return;
      if (!validateRequired(updateData)) return;
      if (!validateChronologicalOrder(updateData)) return;
      // 更新
      // API呼び出し
      const response = await axios.put(
        `${API_BASE_URL}/WorkerSetting/PutWorker`,
        {
          schema: companyCode,
          workerData: updateData,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("作業者情報が更新されました。");
        // 再検索用
        const tagId = updateData.tagId;
        setSearchTagId(tagId);
        handleSearch(tagId);
        // 登録済の作業者を取得
        getWorkerList(companyCode);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("作業者情報の更新に失敗しました。");
        console.error("作業者情報の更新に失敗しました。", error);
      }
    }
  };

  // 削除ボタン押下時処理
  const handleDelete = async (deleteData: WorkerInfo) => {
    try {
      // API呼び出し
      const response = await axios.delete(
        `${API_BASE_URL}/WorkerSetting/DeleteWorker`,
        {
          headers: { "Content-Type": "application/json" },
          data: {
            schema: companyCode,
            workerData: deleteData,
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("作業者情報が削除されました。");
        const tagId = deleteData.tagId;
        setSearchTagId(tagId);
        handleSearch();
        // 登録済の作業者を取得
        getWorkerList(companyCode);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("作業者情報の削除に失敗しました。");
        console.error("作業者情報の削除に失敗しました。", error);
      }
    }
  };

  // 変更有無チェック
  function isChange(checkData: WorkerInfo): boolean {
    // 検索時のデータを取得
    const iniData = searchResults.find((x) => x.id === checkData.id);
    if (
      checkData.name === iniData?.name &&
      checkData.startDate === iniData.startDate &&
      checkData.startTime === iniData.startTime &&
      checkData.endDate === iniData.endDate &&
      checkData.endTime === iniData.endTime
    ) {
      setModalOpen(true);
      setMessage("登録内容が変更されていません。");
      return false;
    }
    return true;
  }

  // 開始～終了の時系列整合性チェック
  function validateChronologicalOrder(checkData: WorkerInfo): boolean {
    // 空の場合のデフォルト値設定
    const startTime = checkData.startTime || "00:00";
    const endTime = checkData.endTime || "00:00";
    const startDateTime = new Date(`${checkData.startDate}T${startTime}`);
    const endDateTime = new Date(`${checkData.endDate}T${endTime}`);

    // 日付の整合性を確認
    if (endDateTime < startDateTime) {
      setModalOpen(true);
      setMessage("作業開始日時は作業終了日時より前に設定してください。");
      return false;
    }
    return true;
  }

  // 必須チェック
  function validateRequired(checkData: WorkerInfo): boolean {
    if (!checkData.name) {
      setModalOpen(true);
      setMessage("作業者名を入力してください。");
      return false;
    }

    if (!checkData.startDate) {
      setModalOpen(true);
      setMessage("作業開始日を入力してください。");
      return false;
    }

    if (!checkData.endDate) {
      setModalOpen(true);
      setMessage("作業終了日を入力してください。");
      return false;
    }
    return true;
  }

  // 検索タグIDのクリア処理
  const handleClear = () => {
    setSearchTagId("");
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (tagId: string) => {
    setSearchTagId(tagId);
    handleClose();
  };

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const [highlightedTagId, setHighlightedTagId] = useState<string | null>(null);

  // スクロール処理
  useLayoutEffect(() => {
    if (searchTagId) {
      let matchedTagId = "";
      const matched = workerList.find((data) =>
        data.tagId.toLowerCase().startsWith(searchTagId.toLowerCase())
      );
      if (matched) {
        matchedTagId = matched.tagId;
      } else {
        matchedTagId = searchTagId;
      }

      const row = rowRefs.current[matchedTagId];
      const container = tableContainerRef.current;

      if (row && container) {
        requestAnimationFrame(() => {
          row.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
        setHighlightedTagId(matchedTagId);
      }
    }
  }, [searchTagId, workerList]);

  return (
    <>
      <MessageModal
        open={modalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      <Box sx={{ display: "flex", width: "100%" }}>
        <Box sx={{ flex: 7 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <TextField
                fullWidth
                label="タグID"
                name="searchTagId"
                value={searchTagId}
                onChange={handleInputChange}
                inputProps={{ maxLength: 4 }}
                InputProps={{
                  endAdornment: searchTagId && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClear} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Grid2>
                ※タグIDを入力した後、新規登録ボタンを押してください。
              </Grid2>
            </Grid2>
            <Grid2
              container
              sx={{
                gap: 2,
                justifyContent: {
                  xs: "center",
                  sm: "flex-end",
                  md: "flex-end",
                  lg: "flex-start",
                },
                alignItems: "flex-start",
                pt: {
                  lg: 1,
                },
              }}
              size={{ xs: 12, sm: 12, md: 12, lg: 6 }}
            >
              <Grid2
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{ width: { xs: "20vh", sm: "30vh" } }}
                  onClick={handleSearchButton}
                >
                  新規登録／検索
                </Button>
              </Grid2>
              <Grid2
                sx={{
                  display: { md: "flex", lg: "none" },
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleClick}
                  sx={{ width: { xs: "20vh", sm: "30vh" } }}
                >
                  登録済のタグから選ぶ
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  {workerList.map((data) => (
                    <MenuItem
                      key={data.tagId}
                      onClick={() => handleSelect(data.tagId)}
                    >
                      {data.tagId}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid2>
            </Grid2>
            <Stack
              direction={{ xs: "column", sm: "row" }} // xsでは縦方向、sm以上では横方向
              spacing={5} // 要素間の余白
              sx={{ flexGrow: 1 }}
            >
              {isSearch && (
                <Grid2
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    p: 1,
                    border: `4px solid ${theme.palette.custom.paperBorderColorSub}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.custom.sForm,
                  }}
                >
                  <Typography fontWeight="fontWeightBold">新規登録</Typography>
                  <SettingWorkerForm
                    data={inputFormData}
                    inputFlg={true}
                    onInput={handleInput}
                    openModal={handleConfirmModalOpen}
                  />
                </Grid2>
              )}
              {searchResults.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 1,
                    border: `4px solid ${theme.palette.custom.paperBorderColorSub}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.custom.sForm,
                  }}
                >
                  <Typography fontWeight="fontWeightBold">登録中</Typography>
                  <Box
                    sx={{
                      p: 1,
                      maxWidth: {
                        xs: "none",
                        sm: bodyWidth,
                        lg: bodyWidth - 300,
                      },
                      minWidth: {
                        xs: "none",
                        sm: bodyWidth,
                        lg: bodyWidth - 300,
                      },
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                      {searchResults.map((item, index) => (
                        <Grid2
                          key={index}
                          sx={{
                            position: "relative",
                            border: `2px solid ${theme.palette.custom.paperBorderColorSub}`,
                            borderRadius: "4px",
                            p: 2,
                            backgroundColor: theme.palette.custom.sForm,
                            minWidth: {
                              xs: "calc(100%)",
                              sm: "calc(100%)",
                              md: "calc(100%)",
                              lg: "calc(50% - 10px)",
                              //lg: "calc(30% - 15px)",
                            },
                            maxWidth: {
                              xs: "calc(100%)",
                              sm: "calc(100%)",
                              md: "calc(100%)",
                              lg: "calc(50% - 10px)",
                              //lg: "calc(30% - 15px)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: "-15px",
                              left: "10px",
                              backgroundColor: theme.palette.custom.sForm,
                              padding: "0 8px",
                              fontSize: "14px",
                              fontWeight: "bold",
                              zIndex: 1,
                            }}
                          >
                            <SettingWorkerStatus
                              startDate={item.startDate}
                              endDate={item.endDate}
                              startTime={item.startTime}
                              endTime={item.endTime}
                            />
                          </Box>
                          <SettingWorkerForm
                            data={item}
                            index={index}
                            inputFlg={false}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                            openModal={handleConfirmModalOpen}
                          />
                        </Grid2>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </Stack>
          </Grid2>
        </Box>
        <Box
          sx={{
            flex: 2,
            p: 2,
            display: {
              xs: "none",
              md: "none",
              lg: "block",
            },
          }}
        >
          <TableContainer
            sx={{
              maxHeight: tableBodyHeight,
              minHeight: tableBodyHeight,
              overflowY: "auto",
              border: `4px solid ${theme.palette.custom.paperBorderColorSub}`,
              borderRadius: "8px",
              backgroundColor: theme.palette.custom.row,
            }}
            ref={tableContainerRef}
          >
            <Table>
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: theme.palette.custom.tableHeader,
                  zIndex: 1,
                }}
              >
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      textAlign: "center", // 水平方向中央揃え
                      verticalAlign: "middle", // 垂直方向の中央揃え
                      color: "white",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center", // 垂直方向の中央揃え
                        justifyContent: "center", // 水平方向の中央揃え
                        position: "relative",
                      }}
                    >
                      <Box sx={{ flexGrow: 1, textAlign: "center" }}>
                        <Typography fontWeight="fontWeightBold">
                          登録者リスト （
                          {convertNumberToFullWidthDigits(workerList.length)}名
                          ）
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
                {/* TextField を追加 */}
                <TableRow>
                  <TableCell
                    colSpan={1}
                    size="small"
                    sx={{
                      textAlign: "center", // 水平方向中央揃え
                      verticalAlign: "middle", // 垂直方向の中央揃え
                      color: "white",
                    }}
                  >
                    タグID
                  </TableCell>
                  <TableCell
                    colSpan={1}
                    size="small"
                    sx={{
                      textAlign: "center", // 水平方向中央揃え
                      verticalAlign: "middle", // 垂直方向の中央揃え
                      color: "white",
                    }}
                  >
                    現在作業者
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workerList?.map((data, index) => (
                  <TableRow
                    key={data.tagId}
                    onClick={() => setSearchTagId(data.tagId)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        data.tagId === highlightedTagId
                          ? theme.palette.custom.check
                          : theme.palette.custom.row,
                    }}
                    ref={(el) => (rowRefs.current[data.tagId] = el)}
                  >
                    <TableCell
                      sx={{
                        textAlign: "center", // 水平方向中央揃え
                        verticalAlign: "middle", // 垂直方向の中央揃え
                      }}
                    >
                      {data.tagId}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center", // 水平方向中央揃え
                        verticalAlign: "middle", // 垂直方向の中央揃え
                      }}
                    >
                      {data.name}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default SettingWorker;
